import { distance as levenshteinDistance } from "fastest-levenshtein";
import type { TextRangeData } from "../types";
import { normalizeMarkdownToPlainText } from "./diffUtils";

/**
 * Validation states for suggestion matching.
 * These indicate how well the suggestion matches the current document content.
 */
export enum ValidationState {
  /** Text found at expected position, context matches */
  VALID = "VALID",
  /** Text found but context differs - content may have shifted */
  CONTEXT_MISMATCH = "CONTEXT_MISMATCH",
  /** Text found at a different occurrence index than expected */
  OCCURRENCE_SHIFTED = "OCCURRENCE_SHIFTED",
  /** Text no longer exists in the document */
  TEXT_NOT_FOUND = "TEXT_NOT_FOUND",
}

/**
 * Result of finding a suggestion's text range in the document.
 */
export interface SuggestionMatchResult {
  /** The validation state of the match */
  state: ValidationState;
  /** Start position in plain text (undefined if not found) */
  start?: number;
  /** End position in plain text (undefined if not found) */
  end?: number;
  /** The normalized text that was searched for */
  normalizedText: string;
  /** The actual occurrence index where text was found (if different from expected) */
  actualOccurrenceIndex?: number;
}

/**
 * Minimum similarity threshold for context matching (0-1).
 * 0.8 = 80% similarity required for context to be considered matching.
 */
export const CONTEXT_SIMILARITY_THRESHOLD = 0.8;

/**
 * Calculate similarity between two strings as a value between 0 and 1.
 * Uses Levenshtein distance normalized by the maximum string length.
 *
 * @param str1 First string to compare
 * @param str2 Second string to compare
 * @returns Similarity score from 0 (completely different) to 1 (identical)
 */
export function calculateSimilarity(str1: string, str2: string): number {
  // Handle edge cases
  if (str1 === str2) return 1;
  if (str1.length === 0 || str2.length === 0) return 0;

  const distance = levenshteinDistance(str1, str2);
  const maxLength = Math.max(str1.length, str2.length);

  return 1 - distance / maxLength;
}

/**
 * Check if context matches with fuzzy matching.
 * Returns true if either:
 * - Expected context is empty/null (no context to verify)
 * - Actual context meets the similarity threshold
 *
 * For prefix context: compares expected with the END of actual (most recent chars)
 * For suffix context: compares expected with the START of actual (first chars)
 *
 * @param expectedContext The context from the suggestion (may be null/undefined)
 * @param actualContext The context extracted from the document
 * @param threshold Minimum similarity required (default: CONTEXT_SIMILARITY_THRESHOLD)
 * @param isPrefix Whether this is a prefix context (affects how we trim actual context)
 */
export function contextMatches(
  expectedContext: string | null | undefined,
  actualContext: string,
  threshold: number = CONTEXT_SIMILARITY_THRESHOLD,
  isPrefix: boolean = true,
): boolean {
  // No expected context means we can't verify - treat as match
  if (!expectedContext) return true;

  // Normalize both contexts for comparison
  const normalizedExpected = normalizeMarkdownToPlainText(expectedContext);
  let normalizedActual = actualContext;

  // Empty normalized expected also means we can't verify
  if (normalizedExpected.length === 0) return true;

  // If actual context is longer than expected, trim to match expected length
  // For prefix: take the last N characters (closest to the match)
  // For suffix: take the first N characters (closest to the match)
  if (normalizedActual.length > normalizedExpected.length) {
    if (isPrefix) {
      normalizedActual = normalizedActual.slice(-normalizedExpected.length);
    } else {
      normalizedActual = normalizedActual.slice(0, normalizedExpected.length);
    }
  }

  return calculateSimilarity(normalizedExpected, normalizedActual) >= threshold;
}

/**
 * Extract context from plain text around a given position.
 *
 * @param plainText The full plain text content
 * @param start Start position of the target text
 * @param end End position of the target text
 * @param contextLength Number of characters to extract for context
 * @returns Object with prefix and suffix context strings
 */
export function extractContext(
  plainText: string,
  start: number,
  end: number,
  contextLength: number = 50,
): { prefix: string; suffix: string } {
  const prefixStart = Math.max(0, start - contextLength);
  const prefix = plainText.slice(prefixStart, start);

  const suffixEnd = Math.min(plainText.length, end + contextLength);
  const suffix = plainText.slice(end, suffixEnd);

  return { prefix, suffix };
}

/**
 * Find all occurrences of a substring in a string.
 *
 * @param text The text to search in
 * @param searchText The text to search for
 * @returns Array of start positions for each occurrence
 */
export function findAllOccurrences(text: string, searchText: string): number[] {
  // Empty search text would cause infinite loop
  if (searchText.length === 0) return [];

  const positions: number[] = [];
  let pos = text.indexOf(searchText);

  while (pos !== -1) {
    positions.push(pos);
    pos = text.indexOf(searchText, pos + 1);
  }

  return positions;
}

/**
 * Find a suggestion's text range in the document content with context verification.
 *
 * This function:
 * 1. Normalizes the textToDelete (converts markdown to plain text)
 * 2. Finds all occurrences of the text in the document
 * 3. Verifies context (prefix/suffix) to find the best match
 * 4. Returns a validation state indicating match quality
 *
 * @param plainText The plain text content of the document
 * @param textRange The text range data from the suggestion
 * @returns Match result with validation state and positions
 */
export function findSuggestionRange(
  plainText: string,
  textRange: TextRangeData,
): SuggestionMatchResult {
  const { textToDelete, occurrenceIndex, prefixContext, suffixContext } =
    textRange;

  // Normalize the markdown text to match plain text format
  const normalizedText = normalizeMarkdownToPlainText(textToDelete);

  // Handle empty text edge case
  if (normalizedText.length === 0) {
    return {
      state: ValidationState.TEXT_NOT_FOUND,
      normalizedText,
    };
  }

  // Find all occurrences
  const occurrences = findAllOccurrences(plainText, normalizedText);

  // No occurrences found
  if (occurrences.length === 0) {
    return {
      state: ValidationState.TEXT_NOT_FOUND,
      normalizedText,
    };
  }

  const targetOccurrence = occurrenceIndex ?? 0;

  // Check if the expected occurrence exists
  if (targetOccurrence < occurrences.length) {
    const start = occurrences[targetOccurrence];
    const end = start + normalizedText.length;
    const { prefix, suffix } = extractContext(plainText, start, end);

    const prefixMatches = contextMatches(
      prefixContext,
      prefix,
      CONTEXT_SIMILARITY_THRESHOLD,
      true,
    );
    const suffixMatches = contextMatches(
      suffixContext,
      suffix,
      CONTEXT_SIMILARITY_THRESHOLD,
      false,
    );

    if (prefixMatches && suffixMatches) {
      // Perfect match: text at expected occurrence with matching context
      return {
        state: ValidationState.VALID,
        start,
        end,
        normalizedText,
        actualOccurrenceIndex: targetOccurrence,
      };
    }

    // Text found at expected occurrence but context doesn't match
    // Try to find a better match at a different occurrence
    const betterMatch = findBestContextMatch(
      plainText,
      normalizedText,
      occurrences,
      prefixContext,
      suffixContext,
      targetOccurrence,
    );

    if (betterMatch) {
      return betterMatch;
    }

    // No better match found - return the expected occurrence with context mismatch
    return {
      state: ValidationState.CONTEXT_MISMATCH,
      start,
      end,
      normalizedText,
      actualOccurrenceIndex: targetOccurrence,
    };
  }

  // Expected occurrence doesn't exist - try to find the text at any occurrence
  // First try to match by context
  const contextMatch = findBestContextMatch(
    plainText,
    normalizedText,
    occurrences,
    prefixContext,
    suffixContext,
  );

  if (contextMatch) {
    return contextMatch;
  }

  // No context match - return the first occurrence as shifted
  const start = occurrences[0];
  const end = start + normalizedText.length;

  return {
    state: ValidationState.OCCURRENCE_SHIFTED,
    start,
    end,
    normalizedText,
    actualOccurrenceIndex: 0,
  };
}

/**
 * Find the occurrence with the best context match.
 *
 * @param plainText The plain text content
 * @param normalizedText The normalized text to find
 * @param occurrences Array of occurrence positions
 * @param prefixContext Expected prefix context
 * @param suffixContext Expected suffix context
 * @param excludeIndex Optional occurrence index to exclude from search
 * @returns Match result if a good match is found, undefined otherwise
 */
function findBestContextMatch(
  plainText: string,
  normalizedText: string,
  occurrences: number[],
  prefixContext: string | null | undefined,
  suffixContext: string | null | undefined,
  excludeIndex?: number,
): SuggestionMatchResult | undefined {
  // If no context to match against, can't find a better match
  if (!prefixContext && !suffixContext) {
    return undefined;
  }

  let bestScore = 0;
  let bestMatch: SuggestionMatchResult | undefined;

  for (let i = 0; i < occurrences.length; i++) {
    if (i === excludeIndex) continue;

    const start = occurrences[i];
    const end = start + normalizedText.length;
    const { prefix, suffix } = extractContext(plainText, start, end);

    // Calculate combined context similarity score
    let score = 0;
    let checks = 0;

    if (prefixContext) {
      const normalizedPrefix = normalizeMarkdownToPlainText(prefixContext);
      if (normalizedPrefix.length > 0) {
        // For prefix, compare with the end of actual prefix (closest to match)
        const trimmedPrefix =
          prefix.length > normalizedPrefix.length
            ? prefix.slice(-normalizedPrefix.length)
            : prefix;
        score += calculateSimilarity(normalizedPrefix, trimmedPrefix);
        checks++;
      }
    }

    if (suffixContext) {
      const normalizedSuffix = normalizeMarkdownToPlainText(suffixContext);
      if (normalizedSuffix.length > 0) {
        // For suffix, compare with the start of actual suffix (closest to match)
        const trimmedSuffix =
          suffix.length > normalizedSuffix.length
            ? suffix.slice(0, normalizedSuffix.length)
            : suffix;
        score += calculateSimilarity(normalizedSuffix, trimmedSuffix);
        checks++;
      }
    }

    if (checks === 0) continue;

    const avgScore = score / checks;

    if (avgScore >= CONTEXT_SIMILARITY_THRESHOLD && avgScore > bestScore) {
      bestScore = avgScore;
      bestMatch = {
        state: ValidationState.OCCURRENCE_SHIFTED,
        start,
        end,
        normalizedText,
        actualOccurrenceIndex: i,
      };
    }
  }

  return bestMatch;
}

/**
 * Check if a match result indicates the suggestion is still usable.
 * VALID and OCCURRENCE_SHIFTED suggestions can be accepted.
 * CONTEXT_MISMATCH may need user verification but is still usable.
 */
export function isMatchUsable(result: SuggestionMatchResult): boolean {
  return result.state !== ValidationState.TEXT_NOT_FOUND;
}

/**
 * Check if a match result indicates high confidence.
 * Only VALID matches have high confidence.
 */
export function isHighConfidenceMatch(result: SuggestionMatchResult): boolean {
  return result.state === ValidationState.VALID;
}
